/**
 * The web CallEngine (packages/core ports.ts). This file stays in the main bundle and is tiny: it owns the observable
 * state, feature detection and the person's own background picture (IndexedDB). Everything heavy — partytracks +
 * rxjs (Realtime SFU), RNNoise (AudioWorklet + WASM) and MediaPipe background effects — lives in ./media.ts and is
 * imported on the first call, preview or test sound.
 */
import type { HuddleParticipant } from "@bundle/contracts";
import { Huddles, type CallEngine, type CallEngineState, type CallJoin, type HuddlePrefs, type PickedFile, type SpeechChunk } from "@bundle/core";
import { captionsSupported, createCaptioner } from "./captions.ts";
import { recordingSupported } from "./recorder.ts";
import { createStore, del, get, set } from "idb-keyval";
import type { StreamRegistry } from "./sources.ts";

export type EngineHost = {
  readonly state: CallEngineState;
  patch(p: Partial<CallEngineState>): void;
  sources: StreamRegistry;
  /** Bearer token for the media proxy. */
  token(): Promise<string | undefined>;
  apiBase: string;
  /** Loadable URL of a background picture (bundled id or "custom"). */
  backgroundUrl(imageId: string): string | undefined;
};

/** The heavy half (./media.ts). */
export interface MediaImpl {
  connect(join: CallJoin): void;
  disconnect(): void;
  setMic(on: boolean): void;
  setCamera(on: boolean): void;
  setScreen(on: boolean): void;
  applyPrefs(prefs: HuddlePrefs): void;
  setRemote(participants: HuddleParticipant[]): void;
  setPreview(on: boolean): void;
  playTestSound(): void;
  backgroundChanged(): void;
  /** My processed microphone (captions). */
  micTrack(): Promise<MediaStreamTrack | undefined>;
  /** Records the mixed call (./recorder.ts). */
  startRecording(): boolean;
  stopRecording(): Promise<{ blob: Blob; mime: string } | undefined>;
}

const customStore = createStore("bundle-huddle", "backgrounds");
const CUSTOM_KEY = "custom";
/** Pictures larger than this are refused (a background is decoded every frame). */
const MAX_CUSTOM_BYTES = 8 * 1024 * 1024;

function detectSupports(): CallEngineState["supports"] {
  const hasMedia = typeof navigator !== "undefined" && navigator.mediaDevices !== undefined;
  let webgl2 = false;
  try {
    webgl2 = typeof document !== "undefined" && document.createElement("canvas").getContext("webgl2") !== null;
  } catch {
    webgl2 = false;
  }
  return {
    noiseFilter: typeof AudioWorkletNode !== "undefined" && typeof WebAssembly !== "undefined",
    background: typeof OffscreenCanvas !== "undefined" && typeof VideoFrame !== "undefined" && webgl2,
    speakerSelection: typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype,
    screenShare: hasMedia && typeof navigator.mediaDevices.getDisplayMedia === "function",
    captions: hasMedia && captionsSupported(),
    recording: hasMedia && recordingSupported(),
  };
}

const bundledUrl = (id: string) => `/huddle/backgrounds/${id}.svg`;

export function createCallEngine(opts: { token: () => Promise<string | undefined>; sources: StreamRegistry; apiBase: string }): CallEngine {
  let state: CallEngineState = {
    connection: "idle",
    published: {},
    devices: [],
    active: {},
    levels: {},
    localLevel: 0,
    remoteVideo: {},
    remoteScreen: {},
    screenSharing: false,
    supports: detectSupports(),
    backgrounds: Object.fromEntries(Huddles.BACKGROUND_IMAGES.map((id) => [id, bundledUrl(id)])),
  };
  const listeners = new Set<() => void>();
  let customUrl: string | undefined;
  let prefs: HuddlePrefs = Huddles.parsePrefs(undefined);
  let remote: HuddleParticipant[] = [];
  let impl: MediaImpl | undefined;
  let loading: Promise<MediaImpl> | undefined;
  const queue: ((m: MediaImpl) => void)[] = [];
  const speechListeners = new Set<(c: SpeechChunk) => void>();
  const capabilityListeners = new Set<() => void>();
  const captioner = createCaptioner({
    micTrack: () => (impl === undefined ? Promise.resolve(undefined) : impl.micTrack()),
    level: () => state.localLevel,
    emit: (c) => {
      for (const l of Array.from(speechListeners)) l(c);
    },
    capabilityChanged: () => {
      for (const l of Array.from(capabilityListeners)) l();
    },
  });

  const host: EngineHost = {
    get state() {
      return state;
    },
    patch(p) {
      state = { ...state, ...p };
      for (const l of Array.from(listeners)) l();
    },
    sources: opts.sources,
    token: opts.token,
    apiBase: opts.apiBase,
    backgroundUrl: (id) => (id === Huddles.CUSTOM_BACKGROUND ? customUrl : Huddles.BACKGROUND_IMAGES.includes(id) ? bundledUrl(id) : undefined),
  };

  // My own picture, if I picked one before.
  void get<Blob>(CUSTOM_KEY, customStore)
    .then((blob) => {
      if (blob === undefined) return;
      customUrl = URL.createObjectURL(blob);
      host.patch({ backgrounds: { ...state.backgrounds, [Huddles.CUSTOM_BACKGROUND]: customUrl } });
      impl?.backgroundChanged();
    })
    .catch(() => undefined);

  /** Runs `fn` on the media implementation, loading it first (the media chunk) when `load`. */
  const withImpl = (fn: (m: MediaImpl) => void, load: boolean) => {
    if (impl !== undefined) {
      fn(impl);
      return;
    }
    if (!load && loading === undefined) return;
    queue.push(fn);
    loading ??= import("./media.ts").then(({ createMedia }) => {
      const m = createMedia(host, prefs, remote);
      impl = m;
      for (const q of queue.splice(0)) q(m);
      return m;
    });
    loading.catch((err: unknown) => {
      console.error("huddle media failed to load", err);
      loading = undefined;
      host.patch({ error: { kind: "media", at: Date.now() } });
    });
  };

  return {
    subscribe(l) {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    getSnapshot: () => state,
    connect: (join) => withImpl((m) => m.connect(join), true),
    disconnect: () => {
      captioner.endCall();
      withImpl((m) => m.disconnect(), false);
    },
    setTranscription: (mode) => captioner.setMode(mode),
    transcriptionCapability: () => captioner.capability(),
    onTranscriptionCapability(l) {
      capabilityListeners.add(l);
      return () => {
        capabilityListeners.delete(l);
      };
    },
    onSpeech(l) {
      speechListeners.add(l);
      return () => {
        speechListeners.delete(l);
      };
    },
    startRecording: () => impl?.startRecording() ?? false,
    async stopRecording() {
      const out = await impl?.stopRecording();
      if (out === undefined) return undefined;
      const ext = out.mime.includes("mp4") ? "mp4" : "webm";
      const stamp = new Date().toISOString().slice(0, 16).replace("T", " ").replace(":", "h");
      return { name: `Huddle recording ${stamp}.${ext}`, mime: out.mime, size: out.blob.size, lastModified: Date.now(), source: out.blob };
    },
    setMic: (on) => withImpl((m) => m.setMic(on), false),
    setCamera: (on) => withImpl((m) => m.setCamera(on), false),
    setScreen: (on) => withImpl((m) => m.setScreen(on), on),
    applyPrefs(p) {
      prefs = p;
      withImpl((m) => m.applyPrefs(p), false);
    },
    setRemote(ps) {
      remote = ps;
      withImpl((m) => m.setRemote(ps), false);
    },
    setPreview: (on) => withImpl((m) => m.setPreview(on), on),
    playTestSound: () => withImpl((m) => m.playTestSound(), true),
    async setCustomBackground(file: PickedFile) {
      const blob = file.source;
      if (!(blob instanceof Blob) || !file.mime.startsWith("image/") || file.size > MAX_CUSTOM_BYTES) return false;
      try {
        await set(CUSTOM_KEY, blob, customStore);
      } catch {
        await del(CUSTOM_KEY, customStore).catch(() => undefined);
      }
      if (customUrl !== undefined) URL.revokeObjectURL(customUrl);
      customUrl = URL.createObjectURL(blob);
      host.patch({ backgrounds: { ...state.backgrounds, [Huddles.CUSTOM_BACKGROUND]: customUrl } });
      impl?.backgroundChanged();
      return true;
    },
  };
}
